<template>
  <div class="flex h-full flex-col">
    <ChatHeader
      title="New Conversation"
      :editable="false"
      @close="$emit('close')"
    />
    <div
      class="flex flex-1 flex-col items-center justify-center p-6 text-center"
    >
      <div class="max-w-md space-y-4">
        <!-- Show AI Reports if user has Insights permission and there are recent reports -->
        <template v-if="$can('use', 'Insights') && recentAiReports.length > 0">
          <img :src="MyIconSrc" class="mx-auto size-[64px]" />
          <h2 class="text-2xl font-bold text-gpx_terracotta-300">
            Recent AI Reports
          </h2>

          <p class="text-gpx_slate-100">
            Here are your latest AI reports from the past 24 hours:
          </p>

          <div class="space-y-2">
            <router-link
              v-for="report in recentAiReports"
              :key="report.id"
              :to="`/insights/${report.id}`"
              class="block cursor-pointer rounded-lg border border-gpx_gray-800 bg-gpx_gray-900 p-3 text-left text-gpx_slate-100 transition-colors hover:bg-gpx_gray-800"
              @click="$emit('close')"
            >
              <div class="font-medium">{{ report.title }}</div>
              <div class="text-sm text-gpx_gray-600">
                {{
                  new Date(
                    report.completed_at || report.created_at,
                  ).toLocaleDateString()
                }}
                {{
                  new Date(
                    report.completed_at || report.created_at,
                  ).toLocaleTimeString()
                }}
              </div>
            </router-link>
          </div>
        </template>

        <!-- Default content when no recent AI reports or no Insights permission -->
        <template v-else>
          <img :src="MyIconSrc" class="mx-auto size-[64px]" />
          <h2 class="text-2xl font-bold text-gpx_terracotta-300">
            Hi, I'm Scout!
          </h2>

          <p class="text-gpx_slate-100">
            I'm here to help answer your questions<br />
            and provide guidance on your projects.
          </p>

          <div
            v-if="placeSuggestionsCount > 0"
            class="flex items-center gap-2 rounded-lg border border-gpx_gray-800 bg-white px-3 py-2 text-left text-gpx_sm text-gpx_slate-100"
            data-testid="scout-welcome-place-suggestions"
          >
            <span aria-hidden="true">📍</span>
            <span class="flex-1">
              You have
              <span class="font-semibold text-gpx_black-500">
                {{ placeSuggestionsCount }} suggested geofence{{
                  placeSuggestionsCount === 1 ? '' : 's'
                }}
                pending</span
              >.
            </span>
            <button
              type="button"
              class="font-semibold text-gpx_terracotta-300 hover:underline"
              data-testid="scout-welcome-place-suggestions-review"
              @click="onReviewSuggestions"
            >
              Review →
            </button>
          </div>

          <div
            v-if="removalSuggestionsCount > 0"
            class="flex items-center gap-2 rounded-lg border border-gpx_gray-800 bg-white px-3 py-2 text-left text-gpx_sm text-gpx_slate-100"
            data-testid="scout-welcome-geofence-removals"
          >
            <span aria-hidden="true">⚠️</span>
            <span class="flex-1">
              You have
              <span class="font-semibold text-gpx_black-500">
                {{ removalSuggestionsCount }} inactive geofence{{
                  removalSuggestionsCount === 1 ? '' : 's'
                }}
                to review</span
              >.
            </span>
            <button
              type="button"
              class="font-semibold text-gpx_terracotta-300 hover:underline"
              data-testid="scout-welcome-geofence-removals-review"
              @click="onReviewRemovals"
            >
              Review →
            </button>
          </div>

          <div
            class="rounded-lg border border-gpx_gray-800 bg-gpx_gray-900 p-4 text-left text-gpx_slate-100"
          >
            <p class="font-bold">You can ask me about:</p>
            <ul class="mt-2 list-disc pl-5 text-left">
              <li>Location of your trackers</li>
              <li>Battery status</li>
            </ul>
          </div>

          <p class="text-left text-sm text-gpx_slate-100">Try asking:</p>

          <div
            @click="startNewConversation(msg)"
            v-for="(msg, index) in exampleMessages"
            :key="index"
            class="cursor-pointer rounded-lg border border-gpx_gray-800 bg-gpx_gray-900 p-1 text-left text-gpx_slate-100"
          >
            {{ msg }}
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import MyIconSrc from '@/assets/scout/red.svg';
import ChatHeader from './ChatHeader.vue';
import getAccountAiReports from '@gpx-spa/queries/src/getAccountAiReports';
import {
  getAccountPlaceSuggestions,
  getAccountGeofenceRemovalSuggestions,
} from '@gpx-spa/queries';
import { useChatStore } from '@/stores/useChatStore';
import * as events from '@/events';

const store = useStore();
const chatStore = useChatStore();
const accountId = computed(() => store.getters['auth/accountId']);

const { count: placeSuggestionsCount } = getAccountPlaceSuggestions(accountId);
const { count: removalSuggestionsCount } =
  getAccountGeofenceRemovalSuggestions(accountId);

function onReviewSuggestions() {
  events.trigger(events.names.PLACE_SUGGESTIONS_REVIEW_OPENED, {
    source: 'scout_welcome',
    suggestion_count: placeSuggestionsCount.value,
  });
  chatStore.openProactiveSuggestions();
}

function onReviewRemovals() {
  chatStore.openProactiveGeofenceRemovals();
}

const fiveDayAgo = new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)
  .toISOString()
  .split('T')[0];

const { data: aiReports } = getAccountAiReports(accountId.value, {
  status: 'completed',
  created_since: fiveDayAgo,
  is_approved: true,
});

const recentAiReports = computed(() => {
  if (!aiReports.value) return [];

  const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

  return aiReports.value.filter((report) => {
    const reportDate = new Date(report.created_at);
    return reportDate >= twentyFourHoursAgo;
  });
});

const exampleMessages = [
  "Which devices that haven't moved in the last 24 hours?",
  'Which devices are outside of a geofence?',
];

const emit = defineEmits(['close', 'populateNewMessage']);

function startNewConversation(msg) {
  emit('populateNewMessage', msg);
}
</script>
